/**
 * Представление героя для экрана профиля: формулы из @hive/shared, без своих чисел.
 */

import {
  autoAttackPower,
  HERO,
  heroHealth,
  heroMana,
  xpToLevel,
} from "@hive/shared";

/** Потолок уровня из баланса (§12.7). */
export const HERO_LEVEL_CAP = HERO.levelCap;

/** Вехи прокачки: уровень → ключ hero:unlock.{level} */
export const HERO_UNLOCK_LEVELS = [
  1, 5, 10, 12, 15, 16, 20, 22, 25, 30, 35, 40, 45, 50,
] as const;

export type HeroUnlockLevel = (typeof HERO_UNLOCK_LEVELS)[number];

export interface HeroStatsView {
  readonly healthMax: number;
  readonly manaMax: number;
  readonly attackPower: number;
  readonly panelSlots: number;
}

export interface HeroXpView {
  readonly current: number;
  readonly levelStart: number;
  readonly nextLevel: number;
  readonly progress: number;
  readonly atCap: boolean;
}

/** Слотов заклинаний на панели по §12.3 (без автоатаки). */
export function heroPanelSlots(level: number): number {
  if (level >= 30) return 4;
  if (level >= 20) return 3;
  if (level >= 10) return 2;
  return 1;
}

export function heroStatsView(level: number): HeroStatsView {
  return {
    healthMax: heroHealth(level),
    manaMax: heroMana(level),
    attackPower: autoAttackPower(level),
    panelSlots: heroPanelSlots(level),
  };
}

/** Прогресс опыта внутри текущего уровня (накопительная кривая §Э6.2). */
export function heroXpView(level: number, xp: number): HeroXpView {
  if (level >= HERO_LEVEL_CAP) {
    return {
      current: xp,
      levelStart: xpToLevel(HERO_LEVEL_CAP),
      nextLevel: xpToLevel(HERO_LEVEL_CAP),
      progress: 1,
      atCap: true,
    };
  }

  const levelStart = level <= 1 ? 0 : xpToLevel(level);
  const nextLevel = xpToLevel(level + 1);
  const span = Math.max(1, nextLevel - levelStart);
  const clamped = Math.min(Math.max(xp, levelStart), nextLevel);
  const progress = (clamped - levelStart) / span;

  return {
    current: xp,
    levelStart,
    nextLevel,
    progress,
    atCap: false,
  };
}

/** Вехи, уже достигнутые игроком. */
export function heroUnlockLevelsReached(level: number): HeroUnlockLevel[] {
  return HERO_UNLOCK_LEVELS.filter((milestone) => level >= milestone);
}
